import React, { useState } from "react";
import { ArrowRightIcon, Loader2Icon, ShuffleIcon, SparklesIcon } from "lucide-react";
import { useHackathon } from "../../contexts/HackathonContext";
import { groupProgress } from "../../utils/progress";
import { simulateEvaluation } from "../../utils/judging";
import { shuffle } from "../../utils/teams";
import { subjectForGroup } from "../../utils/subjects";
import { PitchTimer } from "./PitchTimer";
export function PitchControl() {
  const {
    state,
    setScore,
    setPitchOrder,
    setCurrentPitch,
    markPitched
  } = useHackathon();
  const [evaluating, setEvaluating] = useState(false);
  const groupId = state.pitchOrder[state.currentPitch];
  const profile = state.groups.find(g => g.id === groupId);
  const members = state.students.filter(s => s.groupId === groupId);
  const scored = !!state.scores[groupId];
  const isLast = state.currentPitch === state.pitchOrder.length - 1;
  const evaluate = () => {
    setEvaluating(true);
    window.setTimeout(() => {
      const p = groupProgress(profile, state.videos, groupId);
      setScore(groupId, simulateEvaluation(p.percent));
      markPitched(groupId);
      setEvaluating(false);
    }, 1400);
  };
  const randomize = () => {
    if (state.pitched.length > 0 && !window.confirm("Some groups have already pitched. Reshuffle the order anyway?")) return;
    setPitchOrder(shuffle(state.pitchOrder));
  };
  return <section aria-labelledby="pitch-heading" className="flex flex-col rounded-2xl border border-line bg-surface p-6" data-id="element-64" data-mp="3bkj3lwbnxxjd">
      <p className="text-sm text-muted" data-id="element-65" data-mp="36n0y6fwuu0g4">
        Pitch {state.currentPitch + 1} of {state.pitchOrder.length}
      </p>
      <h2 id="pitch-heading" className="mt-1 text-2xl font-bold" data-id="element-66" data-mp="3lcf8prc62nx3">
        Group {groupId}
        {profile?.productName && <span className="text-muted" data-id="element-67" data-mp="3gi2lr7bheugl"> · {profile.productName}</span>}
      </h2>
      <p className="mt-1 text-sm text-saffron-ink" data-id="element-68" data-mp="2st06250erboq">{subjectForGroup(groupId) ?? "Track not assigned"}</p>
      <p className="mt-1 text-sm text-muted" data-id="element-69" data-mp="2nv81aaenzv34">{members.map(m => m.name).join(", ")}</p>

      <div className="mt-6 border-y border-line py-5" data-id="element-70" data-mp="3dj42ihl4esr1">
        <PitchTimer key={groupId} data-id="element-71" data-mp="3hkyyr7o72fxy" />
        <p className="mt-1 text-xs text-muted" data-id="element-72" data-mp="3hjwaz8m64tm9">5 min pitch, then 3 min Q&A</p>
      </div>

      <div className="mt-5 flex flex-wrap gap-2" data-id="element-73" data-mp="3mcywqukpeyrw">
        <button type="button" onClick={evaluate} disabled={evaluating} className="inline-flex flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-xl bg-bryellow px-4 py-3 text-sm font-bold text-navy transition-[transform,background-color] duration-150 hover:bg-[#ffd633] active:scale-[0.97] disabled:opacity-70" data-id="element-74" data-mp="3mea2gzxpmgln">
          {evaluating ? <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-75" data-mp="38kj1wh1yi722" /> : <SparklesIcon className="h-4 w-4" aria-hidden="true" data-id="element-76" data-mp="iv7a7vpc7713" />}
          {evaluating ? "AI is evaluating" : scored ? "Re-run AI evaluation" : "Run AI evaluation"}
        </button>
        <button type="button" onClick={() => setCurrentPitch(state.currentPitch + 1)} disabled={isLast} className="inline-flex items-center gap-2 whitespace-nowrap rounded-xl border border-line px-4 py-3 text-sm font-semibold transition-colors duration-150 hover:bg-paper disabled:opacity-40" data-id="element-77" data-mp="nw4r84ym1v10">
          Next pitch
          <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-78" data-mp="1tmwvy46w9ylg" />
        </button>
      </div>

      <div className="mt-6" data-id="element-79" data-mp="1pmmik6grw5wk">
        <div className="flex items-center justify-between pb-2" data-id="element-80" data-mp="1pjrxqdtukj2m">
          <h3 className="text-sm font-semibold" data-id="element-81" data-mp="1klyspo4a5v3x">Pitch order</h3>
          <button type="button" onClick={randomize} className="inline-flex items-center gap-1.5 text-xs font-medium text-muted transition-colors duration-150 hover:text-ink" data-id="element-82" data-mp="1fn5axjhynhxy">
            <ShuffleIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-83" data-mp="3a0n3jkx834zu" />
            Randomize
          </button>
        </div>
        <ol className="space-y-1" data-id="element-84" data-mp="3534u7tfamnrb">
          {state.pitchOrder.map((id, i) => {
          const active = i === state.currentPitch;
          const score = state.scores[id];
          return <li key={id} data-id="element-85" data-mp="3i1mc5dmxtvzb" data-mpi={i}>
                <button type="button" onClick={() => setCurrentPitch(i)} className={`flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm transition-colors duration-150 ${active ? "bg-navy text-white" : "hover:bg-paper"}`} data-id="element-86" data-mp="335534t280tfx">
                  <span className={`w-4 tabular-nums ${active ? "text-white/60" : "text-muted"}`} data-id="element-87" data-mp="2z4bdnv94r6kx">{i + 1}</span>
                  <span className="flex-1 font-medium" data-id="element-88" data-mp="2kggkkdr7n07b">Group {id}</span>
                  {score ? <span className={`tabular-nums ${active ? "text-bryellow" : "text-brgreen"}`} data-id="element-89" data-mp="2pdzw35wiwyvk">{score.total.toFixed(1)}</span> : <span className={active ? "text-white/60" : "text-muted"} data-id="element-90" data-mp="2fg2doz1chwav">{active ? "Pitching" : "Waiting"}</span>}
                </button>
              </li>;
        })}
        </ol>
      </div>
    </section>;
}